import { gql } from "@apollo/client";
import { useMemo, useState } from "react";
import {
  useCreateUserRoleMutation,
  useUpdateUserRoleMutation,
  UserRolesQuery,
  PermissionEnum,
} from "~/graphql/__generated__/graphql";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { omit, startCase, upperFirst } from "lodash";
import CheckboxSelect from "~/components/common/forms/CheckboxSelect";
import { Permission, permissionDescriptions, permissionsNotAssignableInUI } from "@caredna-marlin/utils/userRoles";
import { useCurrentOrg } from "~/providers/GraphqlProvider";

const permissionsList = Object.values(Permission).sort();
export const usePermissionOptions = () => {
  const currentPerms = useCurrentOrg()?.perms || [];

  return useMemo(() => {
    const list = permissionsList.map((p, i) => ({
      key: i,
      p: p as any as PermissionEnum,
      label: upperFirst(startCase(p).toLowerCase()),
      disabled: permissionsNotAssignableInUI.includes(p) || !currentPerms.includes(p as any as PermissionEnum),
      help: upperFirst(permissionDescriptions[p]),
    }));

    // startCase gets confused by "KPIs", fix it
    (list.find(o => o.label === "View kp is") || {label: ''}).label = "View KPIs";

    return list
  }, [currentPerms]);
};

type EditRoleDialogProps = {
  onClose: () => void;
  role: UserRolesQuery["userRole"][number];
};

const EditRoleDialog = ({
  onClose,
  role,
}: EditRoleDialogProps) => {
  const [local, setLocal] = useState(role);
  const [createMutation, { loading: createLoading }] = useCreateUserRoleMutation();
  const [updateMutation, { loading: updateLoading }] = useUpdateUserRoleMutation();

  const loading = createLoading || updateLoading;
  const permissionOptions = usePermissionOptions();

  const onSubmit = async () => {
    if (loading) { return; }

    if (local.id) {
      await updateMutation({
        variables: local,
      });
    } else {
      await createMutation({
        variables: omit(local, "id"),
        refetchQueries: ["UserRoles"],
        awaitRefetchQueries: true,
      });
    }

    onClose();
  };

  const selectedPermissions = local.permissions.map(p => permissionOptions.findIndex(o => o.p === p))

  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      onClose={onClose}
    >
      <DialogTitle>
        {local.id ? "Edit User Role" : "New User Role"}
      </DialogTitle>
      <DialogContent>
        <TextField
          required
          margin="normal"
          label="Name"
          fullWidth
          variant="standard"
          disabled={loading}
          value={local?.name || ""}
          onChange={(e) => setLocal({...local, name: e.target.value})}
          autoFocus
        />
        <CheckboxSelect
          sx={{ mt: 2 }}
          fullWidth
          value={selectedPermissions}
          options={permissionOptions.filter(o => !o.disabled || local.permissions.includes(o.p))}
          onChange={(selected) => setLocal({
            ...local,
            permissions: selected.map(i => permissionOptions[i].p)
          })}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button loading={loading} disabled={!local.name} onClick={onSubmit}>
          {local.id ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

gql(`
  mutation CreateUserRole($name: String!, $permissions: [PermissionEnum!]!)
  @permission(requires: manageOrgConfig)
  {
    createUserRole(name: $name, permissions: $permissions) {
      id
      name
      permissions
    }
  }
`);

gql(`
  mutation UpdateUserRole($id: Int!, $name: String!, $permissions: [PermissionEnum!]!)
  @permission(requires: manageOrgConfig)
  {
    updateUserRole(id: $id, name: $name, permissions: $permissions) {
      id
      name
      permissions
    }
  }
`);

export default EditRoleDialog;
